import { CanvasService } from "appworks/graphics/canvas/canvas-service";
import { Orientation } from "appworks/graphics/canvas/orientation";
import { GraphicsService } from "appworks/graphics/graphics-service";
import { CenterPivot } from "appworks/graphics/pixi/group";
import { Services } from "appworks/services/services";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { TweenContract } from "appworks/utils/contracts/tween-contract";
import { Rectangle } from "appworks/utils/geom/rectangle";
import * as TweenJS from "appworks/utils/tween";
import { AbstractSymbolBehaviour } from "slotworks/components/matrix/symbol/behaviours/abstract-symbol-behaviour";
import { SymbolSubcomponent } from "slotworks/components/matrix/symbol/symbol-subcomponent";
import { respinResultDisplay } from "slotworks/debug/integration-debugger/result-displays/respin-result-display";

export class ThemeWinCyclePulseSymbolBehaviour extends AbstractSymbolBehaviour {

    public behaviourName: string = "LandPulse";

    protected masterSymbol: string = "symbol_0_0";

    protected scale: number;

    protected offset: Rectangle;

    constructor(symbol: SymbolSubcomponent, protected animationDuration: number = 200, protected zoomTo: number = 1.1) {
        super(symbol);
        Services.get(CanvasService).onOrientationChange.add((orientation) => this.updateTransform());
    }

    public static(): void {
        super.static();
        this.cleanup();
        this.symbol.setVisible(true);
    }

    public cycle(): Contract<void> {
        return new Contract<void>((resolve) => {
            const tweens = [];

            this.symbol.setVisible(!this.symbol.symbolAnimation);

            [Orientation.LANDSCAPE, Orientation.PORTRAIT].forEach((orientation) => {
                const originalScale = this.symbol.getTransform()[orientation].scale.x;
                const growTween = new TweenJS.Tween(this.symbol.getTransform()[orientation].scale)
                    .to({ x: originalScale * this.zoomTo, y: originalScale * this.zoomTo }, this.animationDuration / 2);

                const shrinkTween = new TweenJS.Tween(this.symbol.getTransform()[orientation].scale)
                    .to({ x: originalScale, y: originalScale }, this.animationDuration / 2);

                growTween.chain(shrinkTween);

                shrinkTween.onComplete(() => {
                    this.updateTransform();
                    this.cleanup();
                    this.symbol.getTransform()[orientation].scale.x = originalScale;
                    this.symbol.getTransform()[orientation].scale.y = originalScale;
                });

                tweens.push(growTween);
            });
            new Parallel(tweens.map((tween) => { return () => TweenContract.wrapTween(tween) })).then(() => { resolve() }).execute();
        });
    }

    public updateTransform() {
        super.updateTransform();

        if (this.behaviourSprite) {
            this.behaviourSprite.landscape.x += this.offset.x;
            this.behaviourSprite.landscape.y += this.offset.y;
            this.behaviourSprite.landscape.width *= this.offset.width * this.scale;
            this.behaviourSprite.landscape.height *= this.offset.height * this.scale;

            // Adjust for if this is the middle of a stack
            let stackOffset = this.symbol.getStackOffsetLandscapeY();
            if (stackOffset) {
                this.behaviourSprite.landscape.y -= stackOffset;
            }

            this.behaviourSprite.portrait.x += this.offset.x;
            this.behaviourSprite.portrait.y += this.offset.y;
            this.behaviourSprite.portrait.width *= this.offset.width * this.scale;
            this.behaviourSprite.portrait.height *= this.offset.height * this.scale;

            // Adjust for if this is the middle of a stack
            stackOffset = this.symbol.getStackOffsetLandscapeY();
            if (stackOffset) {
                this.behaviourSprite.portrait.y -= stackOffset;
            }
        }
    }

    protected createSprite() {
        this.cleanup();

        this.behaviourSprite = Services.get(GraphicsService).createSprite(this.symbol.symbolId);
        CenterPivot(this.behaviourSprite);
        this.animationLayer.add(this.behaviourSprite);
    }

    protected cleanup() {
        if (this.behaviourSprite) {
            this.animationLayer.remove(this.behaviourSprite);
            this.behaviourSprite.destroy();
            this.behaviourSprite = undefined;
            this.symbol.setVisible(true);
        }
    }
}
